import { styled } from "styled-components";
import { colors } from "../../../theme/colors";
import { MdChevronLeft } from "react-icons/md";
import { useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { Spacer } from "../../../components/ui/spacer/spacer.component";
import { wait } from "../../../utils/functions/misc/wait";
import { BackButton } from "../../../components/layout/back-button/back-button.component";

const COVER_FULL_CLIP_PATH = "polygon(0 0, 100% 0, 100% 100%, 0 100%)";

export const ProjecType = () => {
  const [selection, setSelection] = useState<number | null>(null);

  const navigate = useNavigate();

  useEffect(() => {
    if (selection !== null) {
      wait(1000).then(() => {
        navigate(`/${selection === 0 ? "films" : "series"}/create`);
      });
    }
  }, [selection]);

  return (
    <Main>
      <BackButton />
      <Background
        $primary
        $hoverClipPath="polygon(0 0, 65% 0, 45% 100%, 0 100%)"
        $clipPath={
          selection === 0 || window.innerWidth <= 768
            ? COVER_FULL_CLIP_PATH
            : "polygon(0 0, 60% 0, 40% 100%, 0 100%)"
        }
        $active={selection === 0 && window.innerWidth > 768}
        style={{
          backgroundColor: colors.primary.hex,
        }}
      >
        <Title $primary>Film</Title>
        <Spacer direction="bottom" size={10} />
        <ContinueButton
          $primary
          onClick={() => {
            setSelection(0);
          }}
        >
          Create Film
        </ContinueButton>
      </Background>
      <Background
        $hoverClipPath="polygon(55% 0, 100% 0, 100% 100%, 35% 100%)"
        $clipPath={
          selection === 1 || window.innerWidth <= 768
            ? COVER_FULL_CLIP_PATH
            : "polygon(60% 0, 100% 0, 100% 100%, 40% 100%)"
        }
        $active={selection === 1 && window.innerWidth > 768}
        style={{
          backgroundColor: colors.complement.hex,
        }}
      >
        <Title>Series</Title>
        <Spacer direction="bottom" size={10} />
        <ContinueButton
          onClick={() => {
            setSelection(1);
          }}
        >
          Create Series
        </ContinueButton>
      </Background>
    </Main>
  );
};

const Main = styled.div`
  width: 100vw;
  height: 100vh;
  display: flex;

  @media only screen and (max-width: 48em) {
    flex-direction: column;
  }
`;

const Background = styled.div<{
  $hoverClipPath: string;
  $clipPath: string;
  $active: boolean;
  $primary?: boolean;
}>`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: ${({ $primary }) => ($primary ? "flex-start" : "flex-end")};
  padding: clamp(2rem, 6vw, 8rem);

  position: absolute;
  top: 0;
  left: 0;
  height: 100vh;
  width: 100vw;
  transition: all 0.4s ease-out;
  clip-path: ${({ $clipPath }) => $clipPath};
  z-index: ${({ $active }) => ($active ? 2 : 1)};

  @media only screen and (max-width: 48em) {
    top: ${({ $primary }) => ($primary ? 0 : "auto")};
    bottom: ${({ $primary }) => ($primary ? "auto" : 0)};
    height: 50vh;
  }

  @media only screen and (max-width: 26.5625em) {
    align-items: center;
  }

  &:hover {
    clip-path: ${({ $hoverClipPath, $active, $clipPath }) =>
      $active ? $clipPath : $hoverClipPath};
    z-index: 2;

    @media only screen and (max-width: 48em) {
      clip-path: none;
      height: 60vh;
    }
  }

  & > * {
    transition: all 0.8s cubic-bezier(0.36, 1.45, 1, 1.01);
    transform: ${({ $active, $primary }) =>
      $active
        ? `translateX(${$primary ? "" : "-"}50vw) translateX(${
            $primary ? "-" : ""
          }100%)`
        : "none"};
  }

  & > button {
    transform: ${({ $active, $primary }) =>
      $active
        ? `translateX(${$primary ? "" : "-"}50vw) translateX(${
            $primary ? "-" : ""
          }100%) translateX(${$primary ? "" : "-"}2.5rem)`
        : "none"};
  }
`;

const Title = styled.h1<{
  $primary?: boolean;
}>`
  font-size: 5rem;
  color: ${({ $primary }) => ($primary ? colors.white.hex : colors.text.hex)};
  font-weight: 700;
`;

const ContinueButton = styled.button<{
  $primary?: boolean;
}>`
  padding: 1.5rem 3.5rem;
  border-radius: 10rem;
  font-size: 1.8rem;
  font-weight: 600;
  color: ${({ $primary }) => ($primary ? colors.black.hex : colors.white.hex)};
  background-color: ${({ $primary }) =>
    $primary ? colors.white.hex : colors.black.hex};
  outline: none;
  border: none;
  cursor: pointer;
`;
